import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n/translation-module';

import {
  ActivityInterface,
  GetMultiCrmObjectResponseInterface,
} from '@vendasta/crm';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  Input,
  Signal,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  CrmFieldService,
  StandardIds,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import { MatCardModule } from '@angular/material/card';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';

import { getActivityUrl } from './activity-url-builder';
import { SanitizeHtmlPipe } from './sanitize-html.pipe';
import {
  Observable,
  of,
} from 'rxjs';
import {
  map,
  switchMap,
} from 'rxjs/operators';
import {
  CrmObjectDisplayService,
  CrmObjectService,
} from '../../../shared-services';
import { ObjectType } from '../../../tokens-and-interfaces';
import { ActivityExtended } from '../timeline';
import {
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';

@Component({
  selector: 'crm-activity-communication',
  templateUrl: './activity-communication.component.html',
  styleUrls: ['./activity-communication.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, TranslateModule, TranslationModule, MatCardModule, GalaxyAvatarModule, SanitizeHtmlPipe],
})
export class CrmActivityCommunicationComponent {
  private _activity: WritableSignal<ActivityExtended> = signal({});
  private _objectType: WritableSignal<ObjectType> = signal('Contact');
  private _associatedObjectId: WritableSignal<string> = signal('');

  @Input() set activity(activity: ActivityInterface) {
    this._activity.set(activity);
  }

  get activity() {
    return this._activity();
  }

  @Input() set objectType(objectType: ObjectType) {
    this._objectType.set(objectType);
  }

  @Input() set associatedObjectId(crmObjectId: string) {
    this._associatedObjectId.set(crmObjectId);
  }

  @Input() showAvatar = true;

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;
  protected readonly dateFormat = DateFormat.medium;

  associatedCrmObjectName$ = toObservable(this._activity).pipe(switchMap(() => this.getAssociatedObjectName()));
  associatedCrmObjectName = toSignal(this.associatedCrmObjectName$);

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly crmObjectService: CrmObjectService,
    private readonly objectDisplayService: CrmObjectDisplayService,
  ) {}

  cardInfo: Signal<ActivityCardInfo> = computed(() => {
    const title = this.getStringFieldData(this.activity, StandardIds.ActivityCommunicationTitle)
      || this.getStringFieldData(this.activity, StandardIds.ActivityCommunicationSummaryTitle)
      || '';
    const body = this.getStringFieldData(this.activity, StandardIds.ActivityCommunicationBody)
      || this.getStringFieldData(this.activity, StandardIds.ActivityCommunicationSummaryBody)
      || '';

    return {
      activityName: this.getStringFieldData(this.activity, StandardIds.ActivityName) ?? '',
      sourceName: this.getStringFieldData(this.activity, StandardIds.ActivitySourceName),
      title,
      body,
      effectiveDate: this.fieldService.getFieldValueFromCrmObject(this.activity, SystemFieldIds.ActivityEffectiveDate)
        ?.dateValue,
      activityUrl: getActivityUrl(this.getStringFieldData(this.activity, StandardIds.ActivityUrl)),
      activityUrlName: this.getStringFieldData(this.activity, StandardIds.ActivityUrlName) ?? '',
      communicationType: this.getStringFieldData(this.activity, StandardIds.ActivityCommunicationType) ?? '',
      communicationStatus: this.getStringFieldData(this.activity, StandardIds.ActivityCommunicationStatus) ?? '',
    };
  });

  headerText = computed(() => {
    const { communicationType, communicationStatus } = this.cardInfo();
    if (communicationType !== 'Campaign SMS') {
      return '';
    }
    const baseText = this.translateService.instant('ACTIVITY_TYPES.COMMUNICATION_SUBTYPES.CAMPAIGN_SMS') + ' ';
    const nameText = this.associatedCrmObjectName() || '';
    const verbText = this.getStatusText(communicationStatus);
    return `${baseText}${verbText}${nameText}`.trim();
  });

  private getStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.SENT') + ' ';
      case 'delivered':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.DELIVERED') + ' ';
      case 'failed':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.FAILED') + ' ';
      case 'undelivered':
        return this.translateService.instant('ACTIVITY.SMS_TITLE.UNDELIVERED') + ' ';
      default:
        return '';
    }
  }

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  isPhoneCall = computed(() => {
    return this.cardInfo().communicationType === 'phone_call';
  });

  isCampaignSMS = computed(() => {
    return this.cardInfo().communicationType === 'Campaign SMS';
  });

  getAssociatedObjectName(): Observable<string> {
    const associatedContactID = this.activity?.associations?.find(
      (association) => association?.crmObjectType === 'Contact',
    )?.crmObjectId;

    if (associatedContactID) {
      return this.getObjectName(associatedContactID, 'Contact');
    } else {
      return this.useAssociatedObjectID();
    }
  }

  useAssociatedObjectID(): Observable<string> {
    if (this.objectType === 'Contact') {
      return this.getObjectName(this.associatedObjectId, 'Contact');
    }
    if (this.objectType === 'Company') {
      return this.getObjectName(this.associatedObjectId, 'Company');
    }
    return of('');
  }

  private getObjectName(objectID: string, objectName: ObjectType): Observable<string> {
    return this.crmObjectService.getMultiObject(objectName, [objectID]).pipe(
      map((data: GetMultiCrmObjectResponseInterface) => {
        const crmObject = data?.crmObjects?.[0];
        return this.objectDisplayService.getObjectDisplayName(objectName, crmObject);
      }),
    );
  }
}

interface ActivityCardInfo {
  activityName: string;
  sourceName: string;
  title: string;
  body: string;
  effectiveDate: Date | undefined;
  activityUrl: string | null;
  activityUrlName: string;
  communicationType: string;
  communicationStatus: string;
}
